/**
 * Phase-2 client for the redesigned `broker_deals` backend.
 *
 * Talks to the new normalized endpoints under `/api/broker_deals/`:
 *   - GET  /deals                  list parent deals (filterable via query params)
 *   - GET  /deals/{deal_key}       single parent deal by natural key
 *   - GET  /metadata               curated UI column metadata per table_name
 *   - GET  /metadata/tables        list of available `table_name` values
 *
 * Phase 2a adds deal-side child-entity endpoints:
 *   - /deals/{deal_id}/entities | /deal_entities/...
 *   - /deals/{deal_id}/status_history | /deal_status_history/...
 *   - /deals/{deal_id}/approvals | /approvals/...
 *   - /deals/{deal_id}/assignment_recordings | /assignment_recordings/...
 *   - /entities?q=...   (typeahead lookup)
 *
 * The legacy `brokerDealsService.js` (flat `broker_deals` endpoints) was
 * removed with the Pipeline / Density pages; this is the only deals client.
 *
 * Each method takes `{ token, userOid, ... }` and returns the raw axios
 * response — callers destructure `response.data.data` per the standard
 * de-hub envelope ({ success, data, count, ... }).
 */
import dataService from "./dataservice";
import {
  cachedResponse,
  clearingAfter,
  lookupCache,
  metadataCache,
} from "./referenceCache";

const BASE = "/broker_deals";

/**
 * GET /api/broker_deals/deals?{filters}
 *
 * `filters` is a plain object of query-string-ready params. Backend
 * decides which keys it understands; we pass them through unchanged.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {object} [args.filters]   — e.g. { status: "Negotiating", limit: 100 }
 */
const listDeals = ({ token, userOid, filters = {} }) =>
  dataService.request(
    "get",
    `${BASE}/deals`,
    token,
    userOid,
    undefined,
    { params: filters }
  );

/**
 * POST /api/broker_deals/deals/search
 *
 * Server-side filtering for the deals grid. Takes the same FieldFilter /
 * RuleFilter tree `WigFilterPanel` emits, and supports CROSS-TABLE filters:
 *
 *   { field: "wells.spud_date", operator: "gte", value: "2026-01-01" }
 *   { field: "wells", operator: "exists" }        // has at least one well
 *   { field: "wells", operator: "nexists" }       // has none
 *
 * Several AND-ed rules on the same child prefix must be satisfied by the SAME
 * child row, which is what "a horizontal well spudded in 2026" means.
 *
 * Unlike `listDeals` (which pulls a fixed window and filters in the browser),
 * this filters in the database, so results are not capped by what happened to
 * be fetched.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {object} [args.filters]   — RuleFilter tree; omit for "no filter"
 * @param {number} [args.limit]
 * @param {number} [args.offset]
 * @param {boolean} [args.onlyCount]
 */
const searchDeals = ({
  token,
  userOid,
  filters,
  limit = 500,
  offset = 0,
  onlyCount = false,
}) =>
  dataService.request("post", `${BASE}/deals/search`, token, userOid, {
    // An empty rule set means "no filter" — send null rather than an empty
    // group, which the backend would reject ("RuleFilter must contain at
    // least one rule").
    filters: filters && filters.rules && filters.rules.length ? filters : null,
    limit,
    offset,
    only_count: onlyCount,
  });

/**
 * GET /api/broker_deals/deals/{deal_key}
 *
 * Looks the deal up by natural key (`deals.deal_key`), matching the URL
 * convention used in the router. Caller is responsible for URL-encoding
 * — we encode here defensively.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {string|number} args.dealKey
 */
const getDeal = ({ token, userOid, dealKey }) =>
  dataService.getEndpoint(
    `${BASE}/deals/${encodeURIComponent(dealKey)}`,
    token,
    userOid
  );

/**
 * POST /api/broker_deals/deals
 *
 * Create a new parent deal. `body` carries the deal-level fields; the
 * natural key `deal_key` is required and must be unique — a duplicate
 * returns HTTP 409. Unknown fields are rejected with HTTP 422
 * (backend uses `extra='forbid'`). `created_by` is auto-filled from the
 * authenticated user on the server, so don't send it. Serialize the
 * form state with `fromFormValues(values, dealsMetadata)` before passing
 * it here so dates / lookups are coerced to the API shape.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {object} args.body   — deal fields incl. `deal_key`
 */
const createDeal = ({ token, userOid, body }) =>
  dataService.postEndpoint(`${BASE}/deals`, token, userOid, body);

/**
 * PUT /api/broker_deals/deals/{deal_key}
 *
 * Partial update of a deal, addressed by its current natural key. Only the
 * fields present in `body` are written; omitted fields are left untouched.
 * Renaming `deal_key` is permitted but collisions return HTTP 409; unknown
 * fields are rejected with HTTP 422. `updated_by` is auto-set server-side.
 * This is also the assignment path — `brokerage_id` (brokers lookup) and
 * `agent` are ordinary editable deal fields written through this endpoint.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {string|number} args.dealKey   — current natural key (URL-encoded here)
 * @param {object} args.body             — partial deal fields to write
 */
const updateDeal = ({ token, userOid, dealKey, body }) =>
  dataService.updateEndpoint(
    `${BASE}/deals/${encodeURIComponent(dealKey)}`,
    token,
    userOid,
    body
  );

/**
 * POST /api/broker_deals/deals/bulk_update
 *
 * Set the same field(s) across many deals in one call — backs the deals-list
 * "bulk edit selected" action. The deals are addressed by their integer PK
 * (`deals.id`), NOT the natural `deal_key`, so callers pass the numeric ids
 * collected from the selected grid rows.
 *
 * Request body: `{ deal_ids: number[], updates: { <field>: <value> } }`.
 * Response envelope carries `{ success: true, updated: number,
 * skipped: [{ deal_id, reason }] }` — `updated` is the count actually written
 * and `skipped` lists any ids the backend declined (e.g. not found / not
 * permitted) with a human-readable reason. `updated_by` is auto-set
 * server-side.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {number[]} args.dealIds   — integer PKs of the deals to update
 * @param {object} args.updates     — `{ <field>: <value> }` written to each
 */
const bulkUpdateDeals = ({ token, userOid, dealIds, updates }) =>
  dataService.postEndpoint(`${BASE}/deals/bulk_update`, token, userOid, {
    deal_ids: dealIds,
    updates,
  });

/**
 * DELETE /api/broker_deals/deals/{deal_key}
 *
 * Delete a deal by natural key. Cascades to all child entities server-side.
 * 404 if no row matches.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {string|number} args.dealKey
 */
const deleteDeal = ({ token, userOid, dealKey }) =>
  dataService.deleteEndpoint(
    `${BASE}/deals/${encodeURIComponent(dealKey)}`,
    token,
    userOid
  );

/**
 * POST /api/broker_deals/deals/bulk_delete
 *
 * Delete many deals in one call — backs the deals-list "delete selected"
 * action. Like `bulkUpdateDeals`, the deals are addressed by their integer PK
 * (`deals.id`), NOT the natural `deal_key`, so callers pass the numeric ids
 * collected from the selected grid rows. Each delete cascades to the deal's
 * whole subtree server-side (lease/deeds + tracts, wells + well tracts,
 * entities, approvals, status history, contact log, payments, remarks,
 * attachments) and writes its own audit row.
 *
 * Do NOT emulate this by looping the single-deal DELETE: the server does the
 * per-deal audit + orphan cleanup inside one transaction.
 *
 * Request body: `{ deal_ids: number[] }`.
 *
 * Response envelope mirrors bulk_update's partial-success shape:
 * `{ success: true, deleted: number, skipped: [{ deal_id, reason }] }` —
 * `deleted` is the count actually removed and `skipped` lists any ids the
 * backend declined (not found / not permitted / FK still referencing) with a
 * human-readable reason. Callers must surface `skipped` rather than reporting
 * a clean success. NOTE: the endpoint is being added concurrently in de-hub;
 * treat a 404/405 as "not deployed yet" and code defensively against extra
 * fields (and against `failed`/`errors` as alternate names for `skipped`).
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {number[]} args.dealIds   — integer PKs of the deals to delete
 */
const bulkDeleteDeals = ({ token, userOid, dealIds }) =>
  dataService.postEndpoint(`${BASE}/deals/bulk_delete`, token, userOid, {
    deal_ids: dealIds,
  });

/**
 * Drop every cached GET response (see `dataService.clearCache`).
 *
 * The shared axios instance caches GETs and never self-invalidates on a
 * mutation, so a deals list fetched right after a delete would still contain
 * the deleted row. Call this after any delete, before re-fetching / navigating
 * to a list.
 *
 * @returns {Promise<void>}
 */
const invalidateCache = () => dataService.clearCache();

/**
 * GET /api/broker_deals/metadata?table_name=deals
 *
 * Returns curated `UIMetadata` rows for the deals grid. Used to drive both
 * the AG Grid column defs and the Overview tab on the detail page.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {string} [args.tableName="deals"]
 */
const getDealsMetadata = ({ token, userOid, tableName = "deals" }) =>
  getMetadata({ token, userOid, tableName });

/**
 * GET /api/broker_deals/metadata?table_name={tableName}
 *
 * Generic metadata fetcher — same endpoint as `getDealsMetadata`, just with
 * `tableName` required and no default. Used by the deal-side child-entity
 * tabs (deal_entities, deal_status_history, approvals,
 * assignment_recordings) to drive their column defs and modal forms.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {string} args.tableName  — e.g. "deal_entities", "approvals", ...
 */
// Cached for the tab (services/referenceCache); cleared by the metadata
// admin's saves, and expires so other people's edits arrive too.
const getMetadata = ({ token, userOid, tableName }) =>
  cachedResponse(metadataCache, `${userOid}|${tableName}`, () =>
    dataService.request(
      "get",
      `${BASE}/metadata`,
      token,
      userOid,
      undefined,
      { params: { table_name: tableName } }
    )
  );

/**
 * GET /api/broker_deals/metadata/tables
 *
 * Returns the list of `table_name` values the metadata table currently
 * knows about. Used by debugging / admin screens; the regular UI hits
 * `getDealsMetadata` / `getMetadata` directly.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 */
const listMetadataTables = ({ token, userOid }) =>
  dataService.getEndpoint(`${BASE}/metadata/tables`, token, userOid);

// ---------------------------------------------------------------------------
// Deal entities (BUYER / SELLER lookups joined to the entities table)
// ---------------------------------------------------------------------------

/**
 * GET /api/broker_deals/deals/{deal_id}/entities
 * Returns the BUYER / SELLER rows attached to a deal.
 */
const listDealEntities = ({ token, userOid, dealId }) =>
  dataService.getEndpoint(
    `${BASE}/deals/${encodeURIComponent(dealId)}/entities`,
    token,
    userOid
  );

/**
 * POST /api/broker_deals/deal_entities
 * Body keys: deal_id, entity_id, entity_type ('BUYER'|'SELLER'),
 * pct_deal_receiving (0,1]. Response may carry the
 * X-Deal-Entities-Pct-Warning header when totals don't sum to 1.0 yet.
 */
const createDealEntity = ({ token, userOid, body }) =>
  dataService.postEndpoint(`${BASE}/deal_entities`, token, userOid, body);

/**
 * PUT /api/broker_deals/deal_entities/{id}
 * Same warning-header semantics as createDealEntity.
 */
const updateDealEntity = ({ token, userOid, id, body }) =>
  dataService.updateEndpoint(
    `${BASE}/deal_entities/${encodeURIComponent(id)}`,
    token,
    userOid,
    body
  );

/**
 * DELETE /api/broker_deals/deal_entities/{id}
 */
const deleteDealEntity = ({ token, userOid, id }) =>
  dataService.deleteEndpoint(
    `${BASE}/deal_entities/${encodeURIComponent(id)}`,
    token,
    userOid
  );

// ---------------------------------------------------------------------------
// Deal status history (append-only — no PUT)
// ---------------------------------------------------------------------------

/**
 * GET /api/broker_deals/deals/{deal_id}/status_history
 * Returns from_status / to_status / changed_at / changed_by rows ordered
 * DESC by changed_at (newest first).
 */
const listStatusHistory = ({ token, userOid, dealId }) =>
  dataService.getEndpoint(
    `${BASE}/deals/${encodeURIComponent(dealId)}/status_history`,
    token,
    userOid
  );

/**
 * GET /api/broker_deals/deal_status_history  (no deal_id filter)
 *
 * Returns every deal_status_history row — used by the KPI header to find
 * the newest changed_at per deal_id and flag stale open deals.
 */
const listAllStatusHistory = ({ token, userOid }) =>
  dataService.request("get", `${BASE}/deal_status_history`, token, userOid);

/**
 * POST /api/broker_deals/deal_status_history
 */
const createStatusHistory = ({ token, userOid, body }) =>
  dataService.postEndpoint(`${BASE}/deal_status_history`, token, userOid, body);

/**
 * DELETE /api/broker_deals/deal_status_history/{id}
 */
const deleteStatusHistory = ({ token, userOid, id }) =>
  dataService.deleteEndpoint(
    `${BASE}/deal_status_history/${encodeURIComponent(id)}`,
    token,
    userOid
  );

// ---------------------------------------------------------------------------
// Approvals (OFFER / PAY)
// ---------------------------------------------------------------------------

/**
 * GET /api/broker_deals/deals/{deal_id}/approvals
 */
const listApprovals = ({ token, userOid, dealId }) =>
  dataService.getEndpoint(
    `${BASE}/deals/${encodeURIComponent(dealId)}/approvals`,
    token,
    userOid
  );

/**
 * GET /api/broker_deals/approvals/pending
 *
 * Cross-deal Capex approval queue. Returns every pending OFFER / PAY approval
 * across all deals. Each row carries `deal_id, deal_key, deal_status,
 * approval_type (OFFER|PAY), approval_status, id, approval_notes, approver,
 * approved_at, created_at`. Sends `status=PENDING`; pass `approvalType`
 * ("OFFER" | "PAY") to narrow to one type.
 *
 * @returns {Promise} axios response — `{ success, data, count }`.
 */
const listPendingApprovals = ({ token, userOid, approvalType } = {}) => {
  const params = { status: "PENDING" };
  if (approvalType) params.approval_type = approvalType;
  return dataService.request(
    "get",
    `${BASE}/approvals/pending`,
    token,
    userOid,
    {},
    { params }
  );
};

const createApproval = ({ token, userOid, body }) =>
  dataService.postEndpoint(`${BASE}/approvals`, token, userOid, body);

const updateApproval = ({ token, userOid, id, body }) =>
  dataService.updateEndpoint(
    `${BASE}/approvals/${encodeURIComponent(id)}`,
    token,
    userOid,
    body
  );

const deleteApproval = ({ token, userOid, id }) =>
  dataService.deleteEndpoint(
    `${BASE}/approvals/${encodeURIComponent(id)}`,
    token,
    userOid
  );

// ---------------------------------------------------------------------------
// Deal contact log (broker ↔ seller contact events — single flat list)
// ---------------------------------------------------------------------------

/**
 * GET /api/broker_deals/deals/{deal_id}/contact_log
 */
const listDealContactLog = ({ token, userOid, dealId }) =>
  dataService.getEndpoint(
    `${BASE}/deals/${encodeURIComponent(dealId)}/contact_log`,
    token,
    userOid
  );

/**
 * POST /api/broker_deals/deal_contact_log
 * Body keys: deal_id, contact_date, contact_method, contact_notes.
 */
const createDealContactLog = ({ token, userOid, body }) =>
  dataService.postEndpoint(`${BASE}/deal_contact_log`, token, userOid, body);

/**
 * PUT /api/broker_deals/deal_contact_log/{id}
 */
const updateDealContactLog = ({ token, userOid, id, body }) =>
  dataService.updateEndpoint(
    `${BASE}/deal_contact_log/${encodeURIComponent(id)}`,
    token,
    userOid,
    body
  );

/**
 * DELETE /api/broker_deals/deal_contact_log/{id}
 */
const deleteDealContactLog = ({ token, userOid, id }) =>
  dataService.deleteEndpoint(
    `${BASE}/deal_contact_log/${encodeURIComponent(id)}`,
    token,
    userOid
  );

// ---------------------------------------------------------------------------
// Assignment recordings (county-clerk filing record)
// ---------------------------------------------------------------------------

/**
 * GET /api/broker_deals/deals/{deal_id}/assignment_recordings
 */
const listAssignmentRecordings = ({ token, userOid, dealId }) =>
  dataService.getEndpoint(
    `${BASE}/deals/${encodeURIComponent(dealId)}/assignment_recordings`,
    token,
    userOid
  );

const createAssignmentRecording = ({ token, userOid, body }) =>
  dataService.postEndpoint(
    `${BASE}/assignment_recordings`,
    token,
    userOid,
    body
  );

const updateAssignmentRecording = ({ token, userOid, id, body }) =>
  dataService.updateEndpoint(
    `${BASE}/assignment_recordings/${encodeURIComponent(id)}`,
    token,
    userOid,
    body
  );

const deleteAssignmentRecording = ({ token, userOid, id }) =>
  dataService.deleteEndpoint(
    `${BASE}/assignment_recordings/${encodeURIComponent(id)}`,
    token,
    userOid
  );

// ---------------------------------------------------------------------------
// Lookups
// ---------------------------------------------------------------------------

/**
 * GET /api/broker_deals/entities?q={query}
 *
 * Typeahead lookup for the entities table — backs the entity_id Select on
 * the EntitiesTab create/edit modal.
 */
const searchEntities = ({ token, userOid, query = "" }) =>
  dataService.request(
    "get",
    `${BASE}/entities`,
    token,
    userOid,
    undefined,
    { params: query ? { q: query } : {} }
  );

/**
 * POST /api/broker_deals/entities
 *
 * Create an entity on the fly (backs the EntitiesTab "Add Entity" flow —
 * the `entity_id` typeahead had no create path, so a Buyer/Seller couldn't be
 * added until it already existed). Body is `{ name }`; the backend normalizes
 * the name and is IDEMPOTENT — an existing entity with the same normalized
 * name is returned rather than duplicated, so callers just read
 * `resp.data.data.id` / `.name` from the response either way.
 *
 * Returns the raw axios response. Envelope is `{ success, data, ... }` where
 * `data` is the entity row with at least `{ id, name }`.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {string} args.name   — entity display name
 */
const createEntity = ({ token, userOid, name }) =>
  clearingAfter(
    lookupCache,
    dataService.postEndpoint(`${BASE}/entities`, token, userOid, { name })
  );

/**
 * GET /api/broker_deals/entities?include_inactive={bool}
 *
 * Admin list of the shared entity master (the Buyer/Seller vocabulary). Backs
 * the Admin → Entities maintenance page. Unlike `searchEntities` (typeahead,
 * active-only) this returns ALL entities including deactivated ones when
 * `includeInactive=true`, so the admin can see and reactivate soft-deleted
 * rows. Each row carries `{ id, name, normalized_name, active, created_by,
 * created_at, updated_by, updated_at }`.
 *
 * Returns the raw axios response. Envelope is `{ success, data, count }`.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {boolean} [args.includeInactive=false]
 */
const listAllEntities = ({ token, userOid, includeInactive = false }) =>
  cachedResponse(lookupCache, `${userOid}|entities|${includeInactive}`, () =>
    dataService.request(
      "get",
      `${BASE}/entities`,
      token,
      userOid,
      undefined,
      { params: { include_inactive: includeInactive } }
    )
  );

/**
 * PUT /api/broker_deals/entities/{id}
 *
 * Admin-only update of an entity (rename and/or activate/deactivate). Body is
 * `{ name?, active? }` — only keys present are written. Renaming to a name that
 * normalizes to an existing entity's returns HTTP 409 (duplicate). Deactivating
 * (`active: false`) is a soft delete: the row stays listed in the admin view
 * but drops out of the active-only Buyer/Seller picker (`searchEntities`).
 *
 * Returns the raw axios response. Envelope is `{ success, data }` where `data`
 * is the updated entity row.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {string|number} args.id   — entity PK (URL-encoded here)
 * @param {object} args.body        — `{ name?, active? }`
 */
const updateEntity = ({ token, userOid, id, body }) =>
  clearingAfter(
    lookupCache,
    dataService.updateEndpoint(
      `${BASE}/entities/${encodeURIComponent(id)}`,
      token,
      userOid,
      body
    )
  );

/**
 * POST /api/broker_deals/entities/bulk_set_active
 *
 * Activate or deactivate many entities in one call — backs the bulk action on
 * Admin → Entities. Entities are addressed by integer PK, like
 * `bulkUpdateDeals`.
 *
 * `active` is the only field this takes: it is the only one that means the
 * same thing applied to a hundred rows at once. Renames stay on
 * `updateEntity`, where a duplicate-name 409 can name the row it clashed with.
 *
 * Response is `{ success, updated: number, skipped: [{ entity_id, reason }] }`,
 * NOT the standard envelope. `updated` counts rows actually changed, so rows
 * already in the target state report 0 rather than erroring — read both fields
 * before telling the user what happened.
 *
 * The server caps a single call at `ENTITY_BULK_ACTIVE_MAX` (500) ids; callers
 * with a larger selection must chunk. Admin-only (BD.Admin).
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {number[]} args.entityIds — integer PKs of the entities to update
 * @param {boolean} args.active     — target state for every id
 */
const bulkSetEntitiesActive = ({ token, userOid, entityIds, active }) =>
  clearingAfter(
    lookupCache,
    dataService.postEndpoint(`${BASE}/entities/bulk_set_active`, token, userOid, {
      entity_ids: entityIds,
      active,
    })
  );

/**
 * GET /api/broker_deals/brokers?include_inactive={bool}
 *
 * Lookup source for the deal-level `brokerage_id` field (the new
 * source-of-truth `brokers` table that replaces `brokers_xref`). Rows are
 * ordered by `company_name`; ACTIVE-only by default — pass
 * `includeInactive=true` for admin / historical views.
 *
 * Returns the raw axios response. Backend envelope is
 * `{ success, data: BrokerRow[], count }`, where each row carries
 * `{ id, company_name, vendor_code, status, ... }`. Callers wiring this
 * into a `WigMetadataForm` lookupFetcher adapt each row to the library's
 * `{ id, label }` option shape (label = `company_name`).
 *
 * The deals metadata keys this Lookup as `distinct_values_lookup =
 * "brokers_xref"`, so the lookupFetchers map must be keyed `brokers_xref`
 * (the metadata still names the legacy table) even though the data is now
 * served from `/brokers`.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {boolean} [args.includeInactive=false]
 * @param {string} [args.query]  — optional client-side typeahead text; the
 *   backend ignores it (no server-side filter), but we accept it so the
 *   lookupFetcher signature `(query) => ...` stays uniform with the other
 *   lookups. Filtering, if any, is applied by the caller.
 */
const listBrokers = ({ token, userOid, includeInactive = false }) =>
  cachedResponse(lookupCache, `${userOid}|brokers|${includeInactive}`, () =>
    dataService.request(
      "get",
      `${BASE}/brokers`,
      token,
      userOid,
      undefined,
      { params: { include_inactive: includeInactive } }
    )
  );

/**
 * GET /api/broker_deals/aois?include_inactive={bool}
 *
 * Lookup source for the deal-level `aoi_id` field (Area of Interest).
 * Ordered by `name`; active-only by default. Each row carries
 * `{ id, name, ... }`; lookupFetcher callers adapt to `{ id, label }` with
 * label = `name`. The deals metadata keys this Lookup as
 * `distinct_values_lookup = "aois"`.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {boolean} [args.includeInactive=false]
 */
const listAois = ({ token, userOid, includeInactive = false }) =>
  cachedResponse(lookupCache, `${userOid}|aois|${includeInactive}`, () =>
    dataService.request(
      "get",
      `${BASE}/aois`,
      token,
      userOid,
      undefined,
      { params: { include_inactive: includeInactive } }
    )
  );

/**
 * GET /api/broker_deals/aois?include_inactive={bool}
 *
 * Admin list of the AOI master. Backs the Admin → AOIs maintenance page.
 * Same endpoint as the active-only `listAois` (which the deal `aoi_id` lookup
 * consumes); this alias exists so the admin call site reads as an admin one and
 * defaults are explicit, mirroring `listAllOperators` / `listAllEntities`.
 *
 * Each row carries `{ id, name, src_id, normalized_name, active, created_by,
 * created_at, updated_by, updated_at }`. `src_id` is the upstream prospect key
 * (de-hub migration 069) — null for the acreage groupings, which have no
 * upstream record.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {boolean} [args.includeInactive=false]
 */
const listAllAois = ({ token, userOid, includeInactive = false }) =>
  dataService.request(
    "get",
    `${BASE}/aois`,
    token,
    userOid,
    undefined,
    { params: { include_inactive: includeInactive } }
  );

/**
 * POST /api/broker_deals/aois
 *
 * Create an AOI from the Admin → AOIs "Add AOI" modal. Body is
 * `{ name, src_id? }`; `src_id` is omitted entirely when not supplied, since
 * the acreage groupings legitimately have no upstream prospect key.
 *
 * NOTE: unlike `createOperator` / `createEntity` this is NOT idempotent — the
 * backend returns 409 for a name that normalizes onto an existing AOI (active
 * OR inactive) and for an already-assigned `src_id`, rather than handing back
 * the existing row. Those two dedupe silently because they serve an inline
 * create-on-the-fly typeahead; the AOI field has no such path, so a duplicate
 * here is a mistake worth reporting. Callers surface the 409 detail inline on
 * the offending field.
 *
 * Admin-only server-side (BD.Admin): AOIs are curated prospect inventory.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {string} args.name
 * @param {string} [args.srcId]  — upstream prospect key; blank is not sent
 */
const createAoi = ({ token, userOid, name, srcId }) => {
  const body = { name };
  // Only send the key when there's a real one — the backend treats blank as
  // omitted, but keeping it out of the body makes the intent explicit.
  const key = typeof srcId === "string" ? srcId.trim() : "";
  if (key) body.src_id = key;
  return clearingAfter(
    lookupCache,
    dataService.postEndpoint(`${BASE}/aois`, token, userOid, body)
  );
};

/**
 * PUT /api/broker_deals/aois/{id}
 *
 * Admin-only update of an AOI (rename, set/clear the upstream prospect key,
 * and/or activate/deactivate). Body is `{ name?, src_id?, active? }` — only
 * keys present are written, and an explicit `src_id: null` CLEARS the key
 * (distinguishable server-side from an omitted key).
 *
 * 409 means either the name normalizes onto another AOI or the prospect key
 * belongs to one; the detail names the conflicting row. Deactivation
 * (`active: false`) is the soft delete — the row stays on file because
 * `deals.aoi_id` references it, and drops out of the active-only deal picker
 * (`listAois`). Mirrors `updateOperator` / `updateEntity`.
 *
 * Returns the raw axios response. Envelope is `{ success, data }` where `data`
 * is the updated AOI row.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {string|number} args.id   — AOI PK (URL-encoded here)
 * @param {object} args.body        — `{ name?, src_id?, active? }`
 */
const updateAoi = ({ token, userOid, id, body }) =>
  clearingAfter(
    lookupCache,
    dataService.updateEndpoint(
      `${BASE}/aois/${encodeURIComponent(id)}`,
      token,
      userOid,
      body
    )
  );

/**
 * GET /api/broker_deals/deal_status
 *
 * Lookup source for the deal-level `deal_status` field — the curated list
 * of valid status strings (replacing the legacy `DEAL_STATUS` table). The
 * deals metadata keys this Lookup as `distinct_values_lookup = "deal_status"`,
 * so the lookupFetchers map must be keyed `deal_status`.
 *
 * Returns the raw axios response. Backend envelope is
 * `{ success, data, count }`, where each row carries the status string under
 * a `deal_status` key. The stored value IS the status string (no separate
 * id), so lookupFetcher callers map each row to `{ id: v, label: v }` with
 * `v = r.deal_status ?? r.value ?? r`.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 */
const listDealStatuses = ({ token, userOid }) =>
  dataService.getEndpoint(`${BASE}/deal_status`, token, userOid);

/**
 * GET /api/broker_deals/operators?include_inactive={bool}
 *
 * Lookup source for the `wells.operator_id` field. The backend filters to
 * active-only operators by default; pass `includeInactive=true` for admin
 * views.
 */
const listOperators = ({ token, userOid, includeInactive = false }) =>
  dataService.request(
    "get",
    `${BASE}/operators`,
    token,
    userOid,
    undefined,
    { params: { include_inactive: includeInactive } }
  );

/**
 * POST /api/broker_deals/operators
 *
 * Create an operator on the fly (backs the WellsTab "+ Add '<name>'" flow —
 * the `wells.operator_id` typeahead had no create path, so a well couldn't be
 * assigned to a new operator until it already existed) and the Admin →
 * Operators "Add Operator" modal.
 *
 * Body is `{ name, operator_code? }`. `operator_code` is the code an operator
 * carries over from the upstream internal system of record; it is omitted from
 * the body entirely when not supplied (the inline picker never sets one).
 *
 * The backend is IDEMPOTENT, keyed on whichever identity is sent:
 *   - WITH `operator_code` — that code is the unique business key, so an
 *     operator already holding it is returned unchanged (the posted name is
 *     NOT reconciled onto it).
 *   - WITHOUT — falls back to the normalized name among ACTIVE operators, so
 *     the inline "+ Add" flow can't duplicate an operator that already exists.
 * Either way callers just read `resp.data.data.id` / `.name`.
 *
 * NOTE: operator NAMES are no longer unique server-side (de-hub migration
 * 065) — the upstream master holds eight names under two codes each. Supplying
 * an `operator_code` will therefore happily create a second operator sharing
 * an existing name. That is intentional; the client warns about it rather than
 * blocking (see the Admin → Operators duplicate-name notice).
 *
 * Mirrors `createEntity`. Returns the raw axios response. Envelope is
 * `{ success, data, ... }` where `data` is the operator row with at least
 * `{ id, name, operator_code }`.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {string} args.name             — operator display name
 * @param {string} [args.operatorCode]   — upstream operator code, optional
 */
const createOperator = ({ token, userOid, name, operatorCode }) => {
  const body = { name };
  // Only send the key when there's a real code — the backend treats blank as
  // omitted, but keeping it out of the body makes the intent explicit and
  // leaves the name-keyed idempotent path in play for the inline picker.
  const code = typeof operatorCode === "string" ? operatorCode.trim() : "";
  if (code) body.operator_code = code;
  return dataService.postEndpoint(`${BASE}/operators`, token, userOid, body);
};

/**
 * GET /api/broker_deals/operators?include_inactive={bool}
 *
 * Admin list of the operator master. Backs the Admin → Operators maintenance
 * page. Unlike the active-only `listOperators` (which the wells `operator_id`
 * lookup consumes) this returns ALL operators including deactivated ones when
 * `includeInactive=true`, so the admin can see and reactivate soft-deleted
 * rows. Each row carries `{ id, name, operator_code, normalized_name, active,
 * created_by, created_at, updated_by, updated_at }`. `operator_code` is the
 * upstream code (null for operators added by hand). Mirrors `listAllEntities`.
 *
 * Returns the raw axios response. Envelope is `{ success, data, count }`.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {boolean} [args.includeInactive=false]
 */
const listAllOperators = ({ token, userOid, includeInactive = false }) =>
  dataService.request(
    "get",
    `${BASE}/operators`,
    token,
    userOid,
    undefined,
    { params: { include_inactive: includeInactive } }
  );

/**
 * PUT /api/broker_deals/operators/{id}
 *
 * Admin-only update of an operator (rename, set/clear the upstream operator
 * code, and/or activate/deactivate). Body is `{ name?, operator_code?, active? }`
 * — only keys present are written.
 *
 * A rename does NOT 409 on a duplicate name any more: de-hub migration 065
 * dropped the unique index on `operators.name` because the upstream operator
 * master holds eight names under two codes each. `operator_code` is the unique
 * key now, so a 409 from this endpoint means the CODE is already assigned to
 * another operator. Send `operator_code: null` to clear a code (an explicit
 * null is distinguishable server-side from an omitted key).
 *
 * Deactivating (`active: false`) is a soft delete: the row stays listed in the
 * admin view but drops out of the active-only well operator picker
 * (`listOperators`). Mirrors `updateEntity`.
 *
 * Returns the raw axios response. Envelope is `{ success, data }` where `data`
 * is the updated operator row.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {string|number} args.id   — operator PK (URL-encoded here)
 * @param {object} args.body        — `{ name?, operator_code?, active? }`
 */
const updateOperator = ({ token, userOid, id, body }) =>
  dataService.updateEndpoint(
    `${BASE}/operators/${encodeURIComponent(id)}`,
    token,
    userOid,
    body
  );

// ---------------------------------------------------------------------------
// Lease/Deeds (Phase 2b)
// ---------------------------------------------------------------------------

/**
 * GET /api/broker_deals/lease_deeds?deal_id={dealId}
 * Returns the lease_deeds rows attached to a deal.
 */
const listLeaseDeeds = ({ token, userOid, dealId }) =>
  dataService.request(
    "get",
    `${BASE}/lease_deeds`,
    token,
    userOid,
    undefined,
    { params: { deal_id: dealId } }
  );

/**
 * GET /api/broker_deals/lease_deeds  (no deal_id filter)
 *
 * Returns every lease_deed in the database — used by the KPI header to
 * aggregate "expiring soon" counts across all open deals. Backend default
 * is to return all rows when no filter is passed.
 */
const listAllLeaseDeeds = ({ token, userOid }) =>
  dataService.request("get", `${BASE}/lease_deeds`, token, userOid);

/**
 * POST /api/broker_deals/lease_deeds
 * Body must include `deal_id` plus the lease/deed fields.
 */
const createLeaseDeed = ({ token, userOid, body }) =>
  dataService.postEndpoint(`${BASE}/lease_deeds`, token, userOid, body);

/**
 * PUT /api/broker_deals/lease_deeds/{id}
 */
const updateLeaseDeed = ({ token, userOid, id, body }) =>
  dataService.updateEndpoint(
    `${BASE}/lease_deeds/${encodeURIComponent(id)}`,
    token,
    userOid,
    body
  );

/**
 * DELETE /api/broker_deals/lease_deeds/{id}
 * Cascades to recordings + tracts.
 */
const deleteLeaseDeed = ({ token, userOid, id }) =>
  dataService.deleteEndpoint(
    `${BASE}/lease_deeds/${encodeURIComponent(id)}`,
    token,
    userOid
  );

// ---------------------------------------------------------------------------
// Lease/Deed Recordings (Phase 2b)
// ---------------------------------------------------------------------------

/**
 * GET /api/broker_deals/lease_deeds/{lease_deed_id}/recordings
 */
const listLeaseDeedRecordings = ({ token, userOid, leaseDeedId }) =>
  dataService.getEndpoint(
    `${BASE}/lease_deeds/${encodeURIComponent(leaseDeedId)}/recordings`,
    token,
    userOid
  );

const createLeaseDeedRecording = ({ token, userOid, body }) =>
  dataService.postEndpoint(
    `${BASE}/lease_deed_recordings`,
    token,
    userOid,
    body
  );

const updateLeaseDeedRecording = ({ token, userOid, id, body }) =>
  dataService.updateEndpoint(
    `${BASE}/lease_deed_recordings/${encodeURIComponent(id)}`,
    token,
    userOid,
    body
  );

const deleteLeaseDeedRecording = ({ token, userOid, id }) =>
  dataService.deleteEndpoint(
    `${BASE}/lease_deed_recordings/${encodeURIComponent(id)}`,
    token,
    userOid
  );

// ---------------------------------------------------------------------------
// Lease/Deed Tracts (Phase 2b)
// ---------------------------------------------------------------------------

/**
 * GET /api/broker_deals/lease_deeds/{lease_deed_id}/tracts
 */
const listLeaseDeedTracts = ({ token, userOid, leaseDeedId }) =>
  dataService.getEndpoint(
    `${BASE}/lease_deeds/${encodeURIComponent(leaseDeedId)}/tracts`,
    token,
    userOid
  );

/**
 * GET /api/broker_deals/lease_deed_tracts  (no lease_deed_id filter)
 *
 * Returns every lease_deed_tract in the database — used by the KPI header
 * to compute weighted-average BPA across all deals (Σ co_net_acres_delivered
 * is the weight).
 */
const listAllLeaseDeedTracts = ({ token, userOid }) =>
  dataService.request("get", `${BASE}/lease_deed_tracts`, token, userOid);

const createLeaseDeedTract = ({ token, userOid, body }) =>
  dataService.postEndpoint(
    `${BASE}/lease_deed_tracts`,
    token,
    userOid,
    body
  );

const updateLeaseDeedTract = ({ token, userOid, id, body }) =>
  dataService.updateEndpoint(
    `${BASE}/lease_deed_tracts/${encodeURIComponent(id)}`,
    token,
    userOid,
    body
  );

const deleteLeaseDeedTract = ({ token, userOid, id }) =>
  dataService.deleteEndpoint(
    `${BASE}/lease_deed_tracts/${encodeURIComponent(id)}`,
    token,
    userOid
  );

// ---------------------------------------------------------------------------
// Wells (Phase 2b)
// ---------------------------------------------------------------------------

/**
 * GET /api/broker_deals/wells?deal_id={dealId}
 */
const listWells = ({ token, userOid, dealId }) =>
  dataService.request(
    "get",
    `${BASE}/wells`,
    token,
    userOid,
    undefined,
    { params: { deal_id: dealId } }
  );

const createWell = ({ token, userOid, body }) =>
  dataService.postEndpoint(`${BASE}/wells`, token, userOid, body);

const updateWell = ({ token, userOid, id, body }) =>
  dataService.updateEndpoint(
    `${BASE}/wells/${encodeURIComponent(id)}`,
    token,
    userOid,
    body
  );

/**
 * DELETE /api/broker_deals/wells/{id}
 * Cascades to well_tracts + well_tract_regulatory.
 */
const deleteWell = ({ token, userOid, id }) =>
  dataService.deleteEndpoint(
    `${BASE}/wells/${encodeURIComponent(id)}`,
    token,
    userOid
  );

// ---------------------------------------------------------------------------
// Well Tracts (Phase 2b)
// ---------------------------------------------------------------------------

/**
 * GET /api/broker_deals/wells/{well_id}/tracts
 */
const listWellTracts = ({ token, userOid, wellId }) =>
  dataService.getEndpoint(
    `${BASE}/wells/${encodeURIComponent(wellId)}/tracts`,
    token,
    userOid
  );

const createWellTract = ({ token, userOid, body }) =>
  dataService.postEndpoint(`${BASE}/well_tracts`, token, userOid, body);

const updateWellTract = ({ token, userOid, id, body }) =>
  dataService.updateEndpoint(
    `${BASE}/well_tracts/${encodeURIComponent(id)}`,
    token,
    userOid,
    body
  );

const deleteWellTract = ({ token, userOid, id }) =>
  dataService.deleteEndpoint(
    `${BASE}/well_tracts/${encodeURIComponent(id)}`,
    token,
    userOid
  );

// ---------------------------------------------------------------------------
// Well Tract Regulatory (Phase 2b)
// ---------------------------------------------------------------------------

/**
 * GET /api/broker_deals/well_tracts/{well_tract_id}/regulatory
 */
const listWellTractRegulatory = ({ token, userOid, wellTractId }) =>
  dataService.getEndpoint(
    `${BASE}/well_tracts/${encodeURIComponent(wellTractId)}/regulatory`,
    token,
    userOid
  );

const createWellTractRegulatory = ({ token, userOid, body }) =>
  dataService.postEndpoint(
    `${BASE}/well_tract_regulatory`,
    token,
    userOid,
    body
  );

const updateWellTractRegulatory = ({ token, userOid, id, body }) =>
  dataService.updateEndpoint(
    `${BASE}/well_tract_regulatory/${encodeURIComponent(id)}`,
    token,
    userOid,
    body
  );

const deleteWellTractRegulatory = ({ token, userOid, id }) =>
  dataService.deleteEndpoint(
    `${BASE}/well_tract_regulatory/${encodeURIComponent(id)}`,
    token,
    userOid
  );

// ---------------------------------------------------------------------------
// Payments (Phase 2c — polymorphic parent)
// ---------------------------------------------------------------------------
//
// Payments attach to either a `deal` or a `well_tract` via the
// (parent_table, parent_id) pair. The backend `validate_polymorphic_parent`
// validator returns 400 if the pair doesn't reference a real row — UIs that
// pre-fill parent_table/parent_id from the active scope won't trip this
// unless the user manipulates the picker manually.

/**
 * GET /api/broker_deals/payments?parent_table=...&parent_id=...
 */
const listPayments = ({ token, userOid, parentTable, parentId }) =>
  dataService.request(
    "get",
    `${BASE}/payments`,
    token,
    userOid,
    undefined,
    { params: { parent_table: parentTable, parent_id: parentId } }
  );

/**
 * GET /api/broker_deals/payments?parent_table=deal  (no parent_id)
 *
 * Returns every payment whose parent is a deal — used by the KPI header
 * to detect OOP-past-due deals (open deals with no BONUS payment yet).
 */
const listAllPayments = ({ token, userOid, parentTable = "deal" }) =>
  dataService.request(
    "get",
    `${BASE}/payments`,
    token,
    userOid,
    undefined,
    { params: { parent_table: parentTable } }
  );

const createPayment = ({ token, userOid, body }) =>
  dataService.postEndpoint(`${BASE}/payments`, token, userOid, body);

const updatePayment = ({ token, userOid, id, body }) =>
  dataService.updateEndpoint(
    `${BASE}/payments/${encodeURIComponent(id)}`,
    token,
    userOid,
    body
  );

const deletePayment = ({ token, userOid, id }) =>
  dataService.deleteEndpoint(
    `${BASE}/payments/${encodeURIComponent(id)}`,
    token,
    userOid
  );

// ---------------------------------------------------------------------------
// Remarks (Phase 2c — polymorphic parent, append-only)
// ---------------------------------------------------------------------------
//
// Remarks can attach to deal, lease_deed, lease_deed_tract, well, well_tract,
// or payment. Append-only — backend exposes POST + DELETE but NOT PUT.

/**
 * GET /api/broker_deals/remarks?parent_table=...&parent_id=...
 */
const listRemarks = ({ token, userOid, parentTable, parentId }) =>
  dataService.request(
    "get",
    `${BASE}/remarks`,
    token,
    userOid,
    undefined,
    { params: { parent_table: parentTable, parent_id: parentId } }
  );

const createRemark = ({ token, userOid, body }) =>
  dataService.postEndpoint(`${BASE}/remarks`, token, userOid, body);

const deleteRemark = ({ token, userOid, id }) =>
  dataService.deleteEndpoint(
    `${BASE}/remarks/${encodeURIComponent(id)}`,
    token,
    userOid
  );

// ---------------------------------------------------------------------------
// Attachments (Phase 2c — polymorphic parent, multipart upload)
// ---------------------------------------------------------------------------
//
// Attachments can attach to any of 7 parent types: deal, lease_deed,
// lease_deed_tract, well, well_tract, payment, remark. Upload is multipart
// (FormData) — see `dataservice.js`, which detects FormData and omits the
// Content-Type header so axios sets the multipart boundary automatically.
//
// The legacy attachment endpoints that routed on (deal_key) and
// (parent_deal_id) are no longer called from the app.

/**
 * GET /api/broker_deals/attachments?parent_table=...&parent_id=...
 */
const listAttachments = ({ token, userOid, parentTable, parentId }) =>
  dataService.request(
    "get",
    `${BASE}/attachments`,
    token,
    userOid,
    undefined,
    { params: { parent_table: parentTable, parent_id: parentId } }
  );

/**
 * POST /api/broker_deals/attachments  (multipart)
 * Multipart body: file. `parent_table`, `parent_id`, and `attachment_type`
 * are QUERY params — the backend declares them with FastAPI `Query(...)`, not
 * `Form(...)`, so sending them as form fields yields a 422 for the missing
 * query params. `attachmentType` is the user-chosen category from the
 * `(table_name='attachments', field='attachment_type')` pick-list (UAT #17);
 * omitting it (only for legacy callers) leaves the query param off.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {string} args.parentTable
 * @param {string|number} args.parentId
 * @param {File} args.file
 * @param {string} [args.attachmentType] — chosen category (pick-list value)
 */
const uploadAttachment = ({
  token,
  userOid,
  parentTable,
  parentId,
  file,
  attachmentType,
}) => {
  const form = new FormData();
  form.append("file", file);
  const params = { parent_table: parentTable, parent_id: parentId };
  if (attachmentType !== undefined && attachmentType !== null) {
    params.attachment_type = attachmentType;
  }
  return dataService.request("post", `${BASE}/attachments`, token, userOid, form, {
    params,
  });
};

/**
 * GET /api/broker_deals/attachments/{id}/url
 * Returns { url: "https://..." } — short-lived Azure Blob SAS URL. Fetch on
 * every download click; don't cache.
 */
const getAttachmentUrl = ({ token, userOid, id }) =>
  dataService.getEndpoint(
    `${BASE}/attachments/${encodeURIComponent(id)}/url`,
    token,
    userOid
  );

/**
 * DELETE /api/broker_deals/attachments/{id}
 */
const deleteAttachment = ({ token, userOid, id }) =>
  dataService.deleteEndpoint(
    `${BASE}/attachments/${encodeURIComponent(id)}`,
    token,
    userOid
  );

// ---------------------------------------------------------------------------
// Notifications (gap 5 — in-app notification bell + center)
// ---------------------------------------------------------------------------
//
// Server already scopes every read to the caller (recipient_user = me OR
// recipient_role ∈ my roles), so the frontend just calls these — no client
// filtering needed. Each notification row carries:
//   { id, recipient_user, recipient_role, event_type
//     ('approval_requested'|'approval_decided'|'deal_assigned'),
//     title, body, deal_id, deal_key, is_read, created_at, created_by }
// newest first.
//
// DEPRECATED — nothing in this app calls these any more.
//
// There is one bell in the UI, so there is now one table behind it:
// `app_notifications`, read through `appSupportService`. Deal assignment and
// approvals are emitted there too, tagged `app=broker_deals`. Keeping a second
// source is what produced #190 — feedback status changes went to the shared
// table while the bell read this one, so reporters were told nothing for
// weeks and nothing errored.
//
// These wrappers stay only until the de-hub endpoints they call are removed,
// which is the follow-up to #190. Do not wire anything new to them.

/**
 * GET /api/broker_deals/notifications?unread={bool}&limit={n}
 *
 * Returns the caller's notifications, newest first. Backend envelope is
 * `{ success, data, count }`; callers read `resp?.data?.data`.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {boolean} [args.unread]  — when true, only unread rows
 * @param {number}  [args.limit]   — cap the number of rows returned
 */
const listNotifications = ({ token, userOid, unread, limit }) => {
  const params = {};
  if (unread !== undefined) params.unread = unread;
  if (limit !== undefined) params.limit = limit;
  return dataService.request(
    "get",
    `${BASE}/notifications`,
    token,
    userOid,
    undefined,
    { params }
  );
};

/**
 * GET /api/broker_deals/notifications/unread_count
 *
 * Returns `{ success, data: { unread_count }, count }`. Polled by the bell.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 */
const getNotificationUnreadCount = ({ token, userOid }) =>
  dataService.getEndpoint(
    `${BASE}/notifications/unread_count`,
    token,
    userOid
  );

/**
 * POST /api/broker_deals/notifications/{id}/read
 *
 * Marks a single notification read (server scopes to the caller).
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {string|number} args.id
 */
const markNotificationRead = ({ token, userOid, id }) =>
  dataService.postEndpoint(
    `${BASE}/notifications/${encodeURIComponent(id)}/read`,
    token,
    userOid,
    {}
  );

/**
 * POST /api/broker_deals/notifications/read_all
 *
 * Marks every unread notification for the caller read. Returns
 * `{ success, data: { updated } }`.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 */
const markAllNotificationsRead = ({ token, userOid }) =>
  dataService.postEndpoint(
    `${BASE}/notifications/read_all`,
    token,
    userOid,
    {}
  );

// Notification preferences — the per-user mute switches behind the gear in
// the bell. Two of the three event types are ROLE broadcasts (every
// CapexApprover sees every approval request, every BrokerAgent sees every
// reassignment), so without these the bell is noisy by construction.
//
// The server owns the defaults: absence of a stored row means enabled, and
// GET always returns the COMPLETE map, so the client renders whatever keys
// come back rather than hard-coding the vocabulary.

/**
 * GET /api/broker_deals/notifications/prefs
 *
 * Returns the caller's notification preferences as `{ success, data }` where
 * `data` is a map of `event_type -> enabled` (false = muted), always
 * containing every event type the server knows about.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 */
const getNotificationPrefs = ({ token, userOid }) =>
  dataService.getEndpoint(`${BASE}/notifications/prefs`, token, userOid);

/**
 * PUT /api/broker_deals/notifications/prefs
 *
 * Sets the caller's notification preferences. PARTIAL — only the event types
 * present in `prefs` are written, so flipping one switch does not need the
 * others. Returns the caller's complete map after the write, which is what
 * the UI should render (don't assume the local state is authoritative).
 *
 * Muting hides matching rows from both the list and the unread badge;
 * un-muting brings back what arrived while muted.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {Record<string, boolean>} args.prefs — event_type -> enabled
 */
const updateNotificationPrefs = ({ token, userOid, prefs }) =>
  dataService.updateEndpoint(`${BASE}/notifications/prefs`, token, userOid, {
    prefs,
  });

// ---------------------------------------------------------------------------
// Lookups (cont.) — warwick_landman
// ---------------------------------------------------------------------------

/**
 * GET /api/broker_deals/landmen?include_inactive={bool}
 *
 * Lookup source for the deal-level `warwick_landman_id` field — the WIG land
 * team roster. Active-only by default; pass `includeInactive=true` for admin /
 * historical views. Backend envelope is `{ success, data, count }`; each row
 * carries `{ id, full_name, email, active }`. lookupFetcher callers adapt to
 * `{ id, label }` with label = `full_name`.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {boolean} [args.includeInactive=false]
 */
const listLandmen = ({ token, userOid, includeInactive = false }) =>
  cachedResponse(lookupCache, `${userOid}|landmen|${includeInactive}`, () =>
    dataService.request(
      "get",
      `${BASE}/landmen`,
      token,
      userOid,
      undefined,
      { params: { include_inactive: includeInactive ? 1 : 0 } }
    )
  );

/**
 * GET /api/broker_deals/landmen?include_inactive={0|1}
 *
 * Admin list of the WIG land-team roster. Backs the Admin → Landmen
 * maintenance page. Unlike the active-only `listLandmen` (which the deal
 * form's Warwick Landman picker consumes) this returns ALL landmen including
 * deactivated ones when `includeInactive=true`, so an admin can see and
 * reactivate soft-deleted rows. Mirrors `listAllOperators` / `listAllAois`.
 *
 * NOTE the query parameter is 0/1 here, not true/false — same endpoint as
 * `listLandmen`, whose serialization this deliberately matches.
 *
 * Each row carries `{ id, full_name, email, active }`. There are NO audit
 * columns on `warwick_landman` (no created_by / updated_at, unlike operators
 * and entities), so the admin grid has nothing to show for provenance.
 *
 * Returns the raw axios response. Envelope is `{ success, message, data,
 * count }`.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {boolean} [args.includeInactive=false]
 */
const listAllLandmen = ({ token, userOid, includeInactive = false }) =>
  dataService.request(
    "get",
    `${BASE}/landmen`,
    token,
    userOid,
    undefined,
    { params: { include_inactive: includeInactive ? 1 : 0 } }
  );

/**
 * POST /api/broker_deals/landmen
 *
 * Add a landman to the roster from the Admin → Landmen "Add Landman" modal.
 * Body is `{ full_name, email? }`; `email` is omitted from the body entirely
 * when blank, since a roster entry need not have one.
 *
 * NOT idempotent, unlike `createOperator`: `warwick_landman.full_name` is
 * UNIQUE, so posting a name already on the roster is a 409 rather than a
 * silent return of the existing row. Callers surface that inline on the name
 * field (see the Admin → Landmen screen) — reactivating a deactivated landman
 * is the fix, not a second row.
 *
 * Returns the raw axios response. Envelope is `{ success, data }` where `data`
 * is the created row `{ id, full_name, email, active }`.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {string} args.fullName  — the landman's display name (UNIQUE)
 * @param {string} [args.email]   — work email, optional
 */
const createLandman = ({ token, userOid, fullName, email }) => {
  const body = { full_name: fullName };
  const addr = typeof email === "string" ? email.trim() : "";
  if (addr) body.email = addr;
  return clearingAfter(
    lookupCache,
    dataService.postEndpoint(`${BASE}/landmen`, token, userOid, body)
  );
};

/**
 * PUT /api/broker_deals/landmen/{id}
 *
 * Admin-only update of a landman (rename, set/clear the email, and/or
 * activate/deactivate). Body is `{ full_name?, email?, active? }` — only keys
 * present are written, and at least one must be. 404 for an unknown id.
 *
 * A 409 means the new `full_name` already belongs to another landman
 * (`full_name` is UNIQUE), so callers surface it on that field.
 *
 * Deactivating (`active: false`) is a soft delete and the ONLY delete there
 * is: the row stays listed in the admin view but drops out of the active-only
 * Warwick Landman picker (`listLandmen`). Deals reference landmen by id, so
 * history must stay intact. Mirrors `updateOperator` / `updateAoi`.
 *
 * Returns the raw axios response. Envelope is `{ success, data }` where `data`
 * is the updated row.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {string|number} args.id  — warwick_landman PK (URL-encoded here)
 * @param {object} args.body       — `{ full_name?, email?, active? }`
 */
const updateLandman = ({ token, userOid, id, body }) =>
  clearingAfter(
    lookupCache,
    dataService.updateEndpoint(
      `${BASE}/landmen/${encodeURIComponent(id)}`,
      token,
      userOid,
      body
    )
  );

// ---------------------------------------------------------------------------
// Template-driven upload — download the generated workbook, upload it filled
// ---------------------------------------------------------------------------
//
// The single intake path. It replaced a column-mapping wizard (upload/preview,
// upload/fields, upload/suggest_mapping, upload/commit and the import-profile
// CRUD), whose client functions were removed with it — the de-hub endpoints
// still exist but nothing here calls them.
//
// Both endpoints can answer with a SPREADSHEET rather than JSON, and axios has
// to choose `responseType` before it knows which. So these requests always ask
// for a blob and the caller branches: `readBlobJson` on success,
// `isSpreadsheetResponse` + `saveBlobResponse` on a rejected upload, and
// `parseBlobError` for an ordinary error envelope that arrived as a Blob.

/**
 * GET /api/broker_deals/upload/template
 *
 * Downloads the New-Record upload workbook (.xlsx). The file is GENERATED
 * server-side on every request from `ui_broker_deals_metadata` (field labels)
 * and `pick_lists` (dropdown values) — nothing is stored on disk — so a
 * pick-list edit in Admin shows up in the very next download. Don't cache the
 * response; the backend already sends `Cache-Control: no-store`.
 *
 * Tenancy: an external broker's copy lists only their OWN brokerage in the
 * Brokerage dropdown. A broker with no brokerage mapping gets a 403.
 *
 * Returns the raw axios response with `data` as a Blob — see
 * `saveBlobResponse` for turning it into a browser download.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 */
const downloadUploadTemplate = ({ token, userOid }) =>
  dataService.request(
    "get",
    `${BASE}/upload/template`,
    token,
    userOid,
    undefined,
    // `cache: false` opts out of axios-cache-interceptor, which wraps the shared
    // instance and caches all GETs. Caching a binary body risks handing back a
    // re-serialised copy, and the template is meant to be regenerated on every
    // click anyway (the API sends Cache-Control: no-store for the same reason).
    { responseType: "blob", cache: false }
  );

/**
 * POST /api/broker_deals/upload/export — existing deals, in the upload workbook.
 *
 * The other half of `downloadUploadTemplate`: that one hands out the BLANK
 * template, this one hands back the selected deals already filled into the same
 * 16-tab shape, so a user can edit them offline and (once the update loader
 * lands) upload the file back.
 *
 * `dealIds` are integer PKs (`deals.id`), the same key `bulkUpdateDeals` uses —
 * not `deal_key`. The API caps the list at 200; callers should refuse a larger
 * selection before spending a round trip on it.
 *
 * Blob-typed for the same reasons as the template download: the response is a
 * binary workbook, and `cache: false` opts out of axios-cache-interceptor
 * because the bytes reflect live deal data (the API sends
 * `Cache-Control: no-store` to match). On failure the error body is JSON inside
 * a blob — pass it through `parseBlobError` before reading `detail`.
 */
const exportDealsToTemplate = ({ token, userOid, dealIds }) =>
  dataService.request(
    "post",
    `${BASE}/upload/export`,
    token,
    userOid,
    { deal_ids: dealIds },
    { responseType: "blob", cache: false }
  );

/**
 * Trigger a browser download for a blob-typed axios response.
 *
 * Pulls the filename out of `Content-Disposition` when the server sent one so
 * the saved file matches what the API named it, and falls back otherwise.
 *
 * @param {import("axios").AxiosResponse} response — must have been requested
 *   with `responseType: "blob"`.
 * @param {string} fallbackName
 */
const saveBlobResponse = (response, fallbackName) => {
  const disposition =
    response?.headers?.["content-disposition"] ||
    response?.headers?.get?.("content-disposition") ||
    "";
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const filename = match ? decodeURIComponent(match[1]) : fallbackName;

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoking synchronously can cancel the download in some browsers. The
  // guard is for tests: the timer can outlive a torn-down jsdom, and an
  // unhandled "window is not defined" a second later fails the whole run.
  setTimeout(() => {
    if (typeof window !== "undefined") window.URL.revokeObjectURL(url);
  }, 1000);
  return filename;
};

/**
 * An error thrown by a `responseType: "blob"` request carries its JSON error
 * envelope as a Blob, not an object — so the usual `err.response.data.detail`
 * is unreadable. Read it back into text and re-attach the parsed body.
 *
 * @param {Error} err — an axios error from a blob request
 * @returns {Promise<Error>} the same error, with `response.data` parsed
 */
const parseBlobError = async (err) => {
  const data = err?.response?.data;
  if (data && typeof data.text === "function") {
    try {
      err.response.data = JSON.parse(await data.text());
    } catch {
      // Non-JSON body (e.g. an HTML gateway error) — leave it alone.
    }
  }
  return err;
};

/**
 * POST /api/broker_deals/upload/template_commit
 *
 * Upload a filled New-Record template workbook.
 *
 * The response is EITHER JSON (validated / committed) or an .xlsx file (the
 * caller's own workbook with an ERROR column appended, HTTP 422). Axios has to
 * pick `responseType` before it knows which, so we always ask for a blob and
 * let the caller branch on status — see `readBlobJson` for the success path and
 * `saveBlobResponse` for the error path.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {File}   args.file
 * @param {boolean} [args.dryRun=true] — true validates without writing.
 */
const uploadTemplate = ({ token, userOid, file, dryRun = true }) => {
  const form = new FormData();
  form.append("file", file);
  form.append("dry_run", String(Boolean(dryRun)));
  return dataService.request(
    "post",
    `${BASE}/upload/template_commit`,
    token,
    userOid,
    form,
    { responseType: "blob" }
  );
};

/**
 * Read a Blob-typed response body back into a JS object.
 *
 * Needed because `uploadTemplate` always requests a blob (the endpoint can
 * return either JSON or a spreadsheet), so even a clean 200 arrives as a Blob.
 *
 * @param {import("axios").AxiosResponse} response
 * @returns {Promise<object>} the parsed envelope, or `{}` if it wasn't JSON
 */
const readBlobJson = async (response) => {
  const body = response?.data;
  if (!body) return {};
  if (typeof body.text !== "function") return body;
  try {
    return JSON.parse(await body.text());
  } catch {
    return {};
  }
};

/**
 * True when an axios error carries a spreadsheet rather than a JSON envelope —
 * i.e. the annotated error workbook from a rejected upload.
 *
 * @param {Error} err
 */
const isSpreadsheetResponse = (err) => {
  const type =
    err?.response?.headers?.["content-type"] ||
    err?.response?.headers?.get?.("content-type") ||
    err?.response?.data?.type ||
    "";
  return String(type).includes("spreadsheetml");
};

const dealsService = {
  // Phase 1
  listDeals,
  searchDeals,
  getDeal,
  createDeal,
  updateDeal,
  bulkUpdateDeals,
  deleteDeal,
  bulkDeleteDeals,
  invalidateCache,
  getDealsMetadata,
  listMetadataTables,
  // Phase 2a — generic metadata
  getMetadata,
  // Phase 2a — deal_entities
  listDealEntities,
  createDealEntity,
  updateDealEntity,
  deleteDealEntity,
  // Phase 2a — deal_status_history
  listStatusHistory,
  createStatusHistory,
  deleteStatusHistory,
  // Phase 3 — un-scoped lookups for the KPI header
  listAllStatusHistory,
  // Phase 2a — approvals
  listApprovals,
  listPendingApprovals,
  createApproval,
  updateApproval,
  deleteApproval,
  // Phase 2a — deal_contact_log
  listDealContactLog,
  createDealContactLog,
  updateDealContactLog,
  deleteDealContactLog,
  // Phase 2a — assignment_recordings
  listAssignmentRecordings,
  createAssignmentRecording,
  updateAssignmentRecording,
  deleteAssignmentRecording,
  // Phase 2a — lookups
  searchEntities,
  createEntity,
  // Admin — entity master maintenance
  listAllEntities,
  updateEntity,
  bulkSetEntitiesActive,
  // Phase 2b — lookups
  listBrokers,
  listAois,
  listDealStatuses,
  listOperators,
  // Admin — operator master maintenance + create-on-the-fly
  createOperator,
  listAllOperators,
  updateOperator,
  // Admin — AOI master maintenance
  createAoi,
  listAllAois,
  updateAoi,
  listLandmen,
  // Admin — landman roster maintenance
  listAllLandmen,
  createLandman,
  updateLandman,
  // Phase 2b — lease_deeds
  listLeaseDeeds,
  createLeaseDeed,
  updateLeaseDeed,
  deleteLeaseDeed,
  // Phase 3 — un-scoped lease/deed lookups for the KPI header
  listAllLeaseDeeds,
  listAllLeaseDeedTracts,
  // Phase 2b — lease_deed_recordings
  listLeaseDeedRecordings,
  createLeaseDeedRecording,
  updateLeaseDeedRecording,
  deleteLeaseDeedRecording,
  // Phase 2b — lease_deed_tracts
  listLeaseDeedTracts,
  createLeaseDeedTract,
  updateLeaseDeedTract,
  deleteLeaseDeedTract,
  // Phase 2b — wells
  listWells,
  createWell,
  updateWell,
  deleteWell,
  // Phase 2b — well_tracts
  listWellTracts,
  createWellTract,
  updateWellTract,
  deleteWellTract,
  // Phase 2b — well_tract_regulatory
  listWellTractRegulatory,
  createWellTractRegulatory,
  updateWellTractRegulatory,
  deleteWellTractRegulatory,
  // Phase 2c — payments (polymorphic parent)
  listPayments,
  createPayment,
  updatePayment,
  deletePayment,
  // Phase 3 — un-scoped payments for the KPI header
  listAllPayments,
  // Phase 2c — remarks (polymorphic parent, append-only)
  listRemarks,
  createRemark,
  deleteRemark,
  // Phase 2c — attachments (polymorphic parent, multipart upload)
  listAttachments,
  uploadAttachment,
  getAttachmentUrl,
  deleteAttachment,
  // Gap 5 — notifications
  listNotifications,
  getNotificationUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
  getNotificationPrefs,
  updateNotificationPrefs,
  // Template-driven upload
  downloadUploadTemplate,
  exportDealsToTemplate,
  uploadTemplate,
  readBlobJson,
  isSpreadsheetResponse,
  saveBlobResponse,
  parseBlobError,
};

export default dealsService;
